//region :: Imports ::
import {createRef, ReactElement, RefObject} from 'react';
import {isNullOrUndefined, MNullable, toErrorMessage} from '../../Utils';
import MProgressSpinner from './ProgressSpinner';
import MComponent from '../Component';
import MToolbar from '../Controls/Toolbar';
import MMessageBar from '../Controls/MessageBar';
import {evalulateBool, MBoolean, MGridSize} from '../Controls/Control';
import MBlockUI from '../Controls/BlockUI';
import {BlockUITemplateType} from 'primereact/blockui';

//endregion

/** */
export type MPageProps = {
    locked: MBoolean;
}

/** */
type Props = MPageProps & {
    title: string;
    subtitle?: string | null;
    width?: MGridSize;
    toolbar?: ReactElement<MToolbar>;
    children: JSX.Element | JSX.Element[] | string;
    onBeforeLoadData?: () => void;
    onLoadData?: () => Promise<void>;
    onLoadDataError?: (exception: any) => boolean;
    onAfterLoadData?: (exception: MNullable<any>) => void;
}

/** */
type State = {
    loadingData: boolean;
    dataLoaded: boolean;
    dataLoadFailed: boolean;
    dataLoadFailedMessage: MNullable<string>;
}

/** */
class MPage extends MComponent<Props, State> {

    private m_loadingData: boolean;
    private m_dataLoaded: boolean;
    private readonly m_messageBarRef: RefObject<MMessageBar>;

    /** */
    constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_loadingData = false;
        this.m_dataLoaded = false;
        this.m_messageBarRef = createRef<MMessageBar>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {
        this.getPageLockTemplate = this.getPageLockTemplate.bind(this);
    }

    /** */
    private initializeState(): void {

        this.state = {
            loadingData: false,
            dataLoaded: false,
            dataLoadFailed: false,
            dataLoadFailedMessage: null
        };

    }

    /** */
    private getPageLockTemplate(): BlockUITemplateType {
        return (<MProgressSpinner/>);
    }

    /** */
    private async loadData(): Promise<void> {

        if(this.m_loadingData || this.m_dataLoaded) {
            return Promise.resolve();
        }

        let exception: MNullable<any> = null;
        let completed: boolean = false;

        //  These are used to prevent data from being
        //  loaded while a load is in progress and when
        //  the data has already been loaded...
        this.m_loadingData = true;
        this.m_dataLoaded = false;

        //  ...and these are used to trigger a render
        //  of the component so the correct BlockUI
        //  template is displayed.
        this.safeSetState((draft: State) => {

            draft.loadingData = this.m_loadingData;
            draft.dataLoaded = this.m_dataLoaded;
            draft.dataLoadFailed = false;
            draft.dataLoadFailedMessage = null;

        });

        try {

            this.props.onBeforeLoadData?.();
            await this.props.onLoadData?.();
            completed = true;

        } catch(ex: any) {

            exception = ex;

            const handled = this.props.onLoadDataError === undefined
                            ? false : this.props.onLoadDataError(ex);

            if(!handled) {
                throw ex;
            }

        } finally {

            this.m_loadingData = false;
            this.m_dataLoaded = true;

            this.safeSetState((draft: State) => {

                draft.loadingData = this.m_loadingData;
                draft.dataLoaded = this.m_dataLoaded;
                draft.dataLoadFailed = !completed;
                draft.dataLoadFailedMessage = !completed ? toErrorMessage(exception) : null;

            });

            this.props.onAfterLoadData?.(exception);

        }

    }

    /** */
    public async componentDidMount(): Promise<void> {
        await this.loadData();
    }

    /** */
    public render(): ReactElement {

        const showTitle = this.props.title.length > 0;
        const showSubtitle = showTitle && !isNullOrUndefined(this.props.subtitle) && this.props.subtitle!.length > 0;
        const width = this.props.width !== undefined ? this.props.width! : 12;
        const locked = evalulateBool(this.props.locked) || this.state.loadingData;

        return (
            <MBlockUI locked={locked}
                      fullscreen={true}
                      template={this.getPageLockTemplate()}>

                <div className="flex justify-content-center">
                    <div className={`w-12 lg:w-${width}`}>

                        <div className="mshell-content-header">
                            {showTitle ? <div className="text-3xl uppercase font-light m-text-truncate">{this.props.title}</div> : null}
                            {showSubtitle ? <div className="hidden md:inline-flex m-text-truncate-2">{this.props.subtitle}</div> : null}
                        </div>

                        {this.props.toolbar ?? null}
                        <MMessageBar ref={this.m_messageBarRef}/>

                        <div className="mt-2">
                            {this.props.children}
                        </div>

                    </div>
                </div>

            </MBlockUI>
        );

    }

    /** */
    public get messageBar(): MMessageBar {
        return this.m_messageBarRef.current!;
    }

}

export default MPage;
